import { useMemo, useState } from 'react';
import { Modal } from '../ui/Modal.js';
import type { DeckRequestOptions } from '@integrate/contracts';
import {
  computeOneOffCosts,
  hasStageData,
  reviewFindings,
  suggestedDeckOptions,
  type SeparationState,
} from '@integrate/core';
import { api, ApiError, PackDeliveryError } from '../api.js';

/*
  The export dialog: the workbook, and the deck.

  Both exports have been rendered SERVER-SIDE since P3b, which is the divergence worth stating here. The
  app builds them in the browser, so its deck is drawn to whatever depth the live chart happens to be
  expanded to and its workbook goes through whatever filters happen to be applied — neither of which a
  server has, and neither of which should silently shape a colleague's handover file. So both are
  request fields: the depth is asked for, and the filter narrowing is opt-in and off by default.

  A DOWNLOAD, not a fetch. The browser is better at this than we are: it handles the Content-Disposition
  filename, the progress, the cancel and the retry, and a 40MB workbook never passes through JavaScript
  memory. The cost is that a failure lands on a browser error page rather than in this dialog, which is
  why the two preconditions that CAN be checked here — a scenario to export, and a project that is not
  still importing — are checked before the link is offered rather than after it is clicked.

  DEFAULTS ARE DERIVED, NOT REMEMBERED. `suggestedDeckOptions` re-runs every time this opens, which is
  what the app does (orgchart.html:10819): a deck exported from a project with no stage data must not
  carry the previous project's choices. That is also why the three stage-dependent boxes explain
  themselves when the project has no stage data — a checkbox that is off by default and one that is off
  because there is nothing to draw read very differently.
*/

type Tab = 'xlsx' | 'pptx' | 'buyer' | 'steerco';

/** Query-string form of the options, omitting anything left at its default. */
function deckQuery(options: DeckRequestOptions, scenarioId: string): string {
  const params = new URLSearchParams({ scenarioId });
  params.set('depth', String(options.depth));
  params.set('density', options.density);
  for (const key of ['stages', 'target', 'summary', 'bridge', 'stranded', 'tsas', 'oneoff', 'compare'] as const) {
    params.set(key, String(options[key]));
  }
  params.set('bridgeMetric', options.bridgeMetric);
  /*
    Formatted HERE, because it is the reader's locale that matters. The original called
    `toLocaleDateString()` while building the deck, which on a server is the server's locale and
    timezone — so a deck exported from London and one exported from Sydney disagreed about the date on
    every chapter divider.
  */
  params.set('date', new Date().toLocaleDateString());
  return params.toString();
}

export function ExportModal({
  projectId,
  state,
  scenarioId,
  scenarioName,
  scenarioCount,
  canWrite,
  onClose,
}: {
  projectId: string;
  state: SeparationState;
  scenarioId: string;
  scenarioName: string;
  /**
   * How many scenarios the project has. The compare slide's default lives HERE and not in
   * `suggestedDeckOptions` because that derivation sees one state, and a scenario list does not
   * fit through that keyhole — the store has the list, so the dialog carries the count in.
   */
  scenarioCount: number;
  /**
   * Whether this reader may WRITE — the first role fact this dialog has ever needed. Every other
   * export is a read; issuing a SteerCo pack appends an event and stamps a restore point, so a
   * reviewer sees the tab explain itself rather than a button the server would refuse.
   */
  canWrite: boolean;
  onClose(): void;
}): JSX.Element {
  const [tab, setTab] = useState<Tab>('xlsx');
  const [includeMeta, setIncludeMeta] = useState(true);
  // Derived on mount rather than held in a default, so reopening after a change offers the new answer.
  const [deck, setDeck] = useState<DeckRequestOptions>(() => ({
    ...suggestedDeckOptions(state),
    compare: scenarioCount > 1,
  }));
  const staged = useMemo(() => hasStageData(state), [state]);
  const oneOffData = useMemo(() => computeOneOffCosts(state).n > 0, [state]);
  /*
    CHALLENGE MODE: what a reviewer will find in this pack, before it is a pack.

    A pack is the one export that leaves a permanent mark — an event in the change log and a restore
    point somebody may put the project back to — so it is the one place worth asking "are you sure"
    about the CONTENT rather than the click. `reviewFindings` is pure and reads the state this dialog
    already holds, so this costs one pass over the design and no round trip.

    ADVISORY, AND THE BUTTON STAYS LIVE. The house rule everywhere else in this app is that a refusal
    names the offending thing rather than disabling the control, and the reasons apply with unusual
    force here: half of these findings are decisions nobody has taken yet, which is the normal state
    of a project, and a pack showing an unfinished design to a committee is frequently the entire
    point of the meeting. What must not happen is issuing one WITHOUT KNOWING — so the misstated half
    is stated where the retry impulse forms, and the undecided half is offered as a count.
  */
  const findings = useMemo(() => reviewFindings(state), [state]);
  const misstated = findings.filter((f) => f.severity === 'misstated');
  const undecided = findings.filter((f) => f.severity === 'undecided');
  /*
    The pack's controls. The name is what the committee will call the moment; the rest of the pack
    rides on the DECK tab's choices deliberately — one dialog state, so the deck somebody tuned and
    the deck inside the pack cannot differ. Issue state is a tiny machine: idle → working → done
    with the change number, or an error the reader can act on — a POST cannot lean on the browser's
    download page the way the href exports do.
  */
  const [packName, setPackName] = useState(() => `SteerCo ${new Date().toLocaleDateString()}`);
  const [packBusy, setPackBusy] = useState(false);
  const [packError, setPackError] = useState<string | null>(null);
  const [packIssued, setPackIssued] = useState<number | null>(null);

  const issuePack = async (): Promise<void> => {
    setPackBusy(true);
    setPackError(null);
    setPackIssued(null);
    /*
      Two failures, opposite advice — so two try blocks. A refused pack never happened, and the
      reader should retry freely. But once `steercoPack` resolves (or throws `PackDeliveryError`),
      the pack EXISTS — event committed, restore point stamped — and a reader told only "it failed"
      would click again and mint a second, real pack. So everything after the act reports as
      issued-with-a-delivery-problem, with the warning where the retry impulse forms.
    */
    let pack: Awaited<ReturnType<typeof api.steercoPack>>;
    try {
      pack = await api.steercoPack(projectId, {
        ...deck,
        scenarioId,
        name: packName.trim(),
        date: new Date().toLocaleDateString(),
      });
    } catch (e) {
      if (e instanceof PackDeliveryError) {
        setPackIssued(e.seq);
        setPackError(
          `The pack was issued as change #${e.seq}, but the download did not complete. ` +
            'Issuing again would create a second pack — check your downloads first.',
        );
      } else {
        setPackError(e instanceof ApiError ? e.message : 'The pack could not be rendered.');
      }
      setPackBusy(false);
      return;
    }
    try {
      /*
        The one fetch-to-blob download in the app, because the act and the file arrive together.
        The object URL is revoked on the next tick — the click has already handed the blob to the
        browser's download machinery by then.
      */
      const url = URL.createObjectURL(pack.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = pack.filename;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setPackIssued(pack.seq);
    } catch {
      setPackIssued(pack.seq);
      setPackError(
        `The pack was issued as change #${pack.seq}, but the browser could not start the download. ` +
          'Issuing again would create a second pack.',
      );
    } finally {
      setPackBusy(false);
    }
  };

  const set = <K extends keyof DeckRequestOptions>(key: K, value: DeckRequestOptions[K]): void =>
    setDeck((d) => ({ ...d, [key]: value }));

  const xlsxHref =
    `/api/projects/${projectId}/export.xlsx?scenarioId=${encodeURIComponent(scenarioId)}` +
    `&includeMeta=${includeMeta}`;
  const pptxHref = `/api/projects/${projectId}/export.pptx?${deckQuery(deck, scenarioId)}`;
  const buyerHref = `/api/projects/${projectId}/export.buyer.xlsx?scenarioId=${encodeURIComponent(scenarioId)}`;

  const CHAPTERS: {
    key: 'summary' | 'target' | 'bridge' | 'stranded' | 'stages' | 'tsas' | 'oneoff' | 'compare';
    label: string;
    note: string;
  }[] = [
    { key: 'summary', label: 'Separation summary', note: 'One slide of headline numbers.' },
    {
      key: 'stages',
      label: 'A chapter per stage',
      note: staged
        ? 'Splits the carve-out chapter into Perimeter, Day 1 and Day 2.'
        : 'Nothing to split yet — no build-ups, TSAs or stranded plans.',
    },
    {
      key: 'target',
      label: 'Target org',
      note: state.targetEnabled ? 'The blueprint and who is placed in it.' : 'No target design on this project.',
    },
    {
      key: 'bridge',
      label: 'Stage bridge',
      note: staged ? 'How the population moves between stages.' : 'Needs stage data to draw.',
    },
    {
      key: 'stranded',
      label: 'Stranded cost',
      note: staged ? 'What RemainCo is left carrying.' : 'Needs a stranded plan to draw.',
    },
    {
      key: 'tsas',
      label: 'TSA exit timeline',
      note: state.design.tsas.length
        ? 'Every agreement as a bar, replacement arrivals as dots.'
        : 'No TSAs recorded yet.',
    },
    {
      key: 'oneoff',
      label: 'One-off costs',
      note: oneOffData
        ? 'Severance by population, hiring and retention, walked to the total.'
        : 'No redundancies or planned positions yet.',
    },
    {
      key: 'compare',
      label: 'Scenario comparison',
      note:
        scenarioCount > 1
          ? `All ${scenarioCount} scenarios priced over the same roster, one row each.`
          : 'Only one scenario on this project.',
    },
  ];

  return (
    <Modal
      testId="export-modal"
      title="Export"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button onClick={onClose}>Done</button>
        </>
      }
    >
      <div className="tabs" role="tablist" aria-label="Export formats">
        <button
          role="tab"
          aria-selected={tab === 'xlsx'}
          data-testid="export-tab-xlsx"
          onClick={() => setTab('xlsx')}
        >
          Excel (full state)
        </button>
        <button
          role="tab"
          aria-selected={tab === 'pptx'}
          data-testid="export-tab-pptx"
          onClick={() => setTab('pptx')}
        >
          PowerPoint
        </button>
        <button
          role="tab"
          aria-selected={tab === 'buyer'}
          data-testid="export-tab-buyer"
          onClick={() => setTab('buyer')}
        >
          Buyer pack (redacted)
        </button>
        <button
          role="tab"
          aria-selected={tab === 'steerco'}
          data-testid="export-tab-steerco"
          onClick={() => setTab('steerco')}
        >
          SteerCo pack
        </button>
      </div>

      <p className="muted small">
        Exporting <strong>{scenarioName}</strong>. Other scenarios are untouched — switch scenario to
        export a different one.
      </p>

      {tab === 'xlsx' && (
        <div data-testid="export-pane-xlsx">
          <p className="muted">
            The full project state: every person, build-up, decision, head designation and setting.
            Re-importable, so this is the file that restores a project rather than describes it.
          </p>
          <label className="check">
            <input
              type="checkbox"
              data-testid="export-include-meta"
              checked={includeMeta}
              onChange={(e) => setIncludeMeta(e.target.checked)}
            />
            <span>
              Include the metadata sheet
              <span className="muted small"> — clear it for a data-only handover, which cannot be re-imported.</span>
            </span>
          </label>
          <p>
            <a
              className="btn"
              data-testid="export-download-xlsx"
              href={xlsxHref}
              onClick={onClose}
            >
              Download .xlsx
            </a>
          </p>
        </div>
      )}

      {tab === 'buyer' && (
        <div data-testid="export-pane-buyer">
          <p className="muted">
            The counterparty-facing workbook: stage headcounts, by-country and by-unit population
            counts, a pseudonymised person list and the TSA register&rsquo;s commercial terms. No
            names, identifiers, salaries, job titles, locations or reporting lines — and none of
            RemainCo&rsquo;s own numbers (stranded cost, mitigation plans, retention). The only cost
            figure is one approximate Day-1 total; compensation detail belongs in a clean-team
            disclosure, where subtraction cannot single anyone out.
          </p>
          <p className="muted small">
            Person codes are stable across versions of this pack, so diligence Q&amp;A can reference
            them — but they cannot be mapped back to individuals without this project&rsquo;s data.
            Review the file before it leaves the deal team, as with any disclosure.
          </p>
          <p>
            <a
              className="btn"
              data-testid="export-download-buyer"
              href={buyerHref}
              onClick={onClose}
            >
              Download buyer pack
            </a>
          </p>
        </div>
      )}

      {tab === 'steerco' && (
        <div data-testid="export-pane-steerco">
          <p className="muted">
            The deck and the workbook rendered as one act, zipped with a manifest. Issuing a pack
            records an event in the change log and stamps a restore point at the same change — so
            the files are provably a photograph of that moment, the project can be put back to
            exactly what was presented, and the next pack&rsquo;s &ldquo;What moved&rdquo; slide
            starts where this one ends.
          </p>
          <p className="muted small">
            The deck inside the pack uses the choices on the PowerPoint tab, plus the &ldquo;What
            moved since the last SteerCo&rdquo; slide, which only a pack can carry.
          </p>
          {misstated.length > 0 && (
            <div data-testid="steerco-challenge">
              <p>
                <strong>
                  {misstated.length}{' '}
                  {misstated.length === 1 ? 'thing in this design' : 'things in this design'} will go
                  into the pack stating one thing and counting another.
                </strong>
              </p>
              <ul className="diag-list">
                {misstated.map((f) => (
                  <li key={f.id}>{f.title}</li>
                ))}
              </ul>
              <p className="muted small">
                The pack can still be issued — this is a photograph, and sometimes the point is to
                photograph exactly where things stand. But a pack is a change number somebody may
                restore to, so it is worth knowing before rather than explaining after. Diagnostics
                has the detail.
              </p>
            </div>
          )}
          {misstated.length === 0 && undecided.length > 0 && (
            <p className="muted small" data-testid="steerco-challenge-clean">
              Nothing in this design contradicts itself. {undecided.length}{' '}
              {undecided.length === 1 ? 'decision has' : 'decisions have'} not been taken yet — see
              Diagnostics for what the room is likely to ask.
            </p>
          )}

          {canWrite ? (
            <>
              <label>
                Pack name
                <input
                  data-testid="steerco-name"
                  value={packName}
                  onChange={(e) => setPackName(e.target.value)}
                  maxLength={200}
                />
              </label>
              <p>
                <button
                  className="btn"
                  data-testid="steerco-issue"
                  disabled={packBusy || packName.trim().length === 0}
                  onClick={() => void issuePack()}
                >
                  {packBusy ? 'Rendering the pack…' : 'Issue & download'}
                </button>
              </p>
              {packError !== null && (
                <p className="muted" data-testid="steerco-error">
                  {packError}
                </p>
              )}
              {packIssued !== null && (
                <p data-testid="steerco-issued">
                  Issued as change <strong>#{packIssued}</strong> — the change log records it, and
                  restore point <strong>#{packIssued - 1}</strong>, stamped in the same act, holds
                  exactly the state in these files.
                </p>
              )}
            </>
          ) : (
            /*
              Rendered-not-disabled: the reason, where the button would be. Deliberately NOT "raise
              a proposal" — a pack cannot be proposed (the event and the rendered files are one
              act), and pointing a reviewer at a door that is locked for everyone would be worse
              than naming the person who can open this one.
            */
            <p className="muted" data-testid="steerco-readonly">
              Issuing a pack records a change, which this role cannot do — ask an editor or the
              owner to issue it.
            </p>
          )}
        </div>
      )}

      {tab === 'pptx' && (
        <div data-testid="export-pane-pptx">
          <p className="muted">
            The org chart as native, editable PowerPoint shapes — boxes and connectors, not a picture
            — with chapters for as-is, RemainCo and the carve-out.
          </p>

          <div className="row">
            <label>
              Depth
              <select
                data-testid="export-depth"
                value={deck.depth}
                onChange={(e) => set('depth', Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>
                    {n} level{n === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Density
              <select
                data-testid="export-density"
                value={deck.density}
                onChange={(e) => set('density', e.target.value as DeckRequestOptions['density'])}
              >
                <option value="detailed">Detailed</option>
                <option value="balanced">Balanced</option>
                <option value="compact">Compact</option>
              </select>
            </label>
          </div>
          {/*
            Asked for rather than read off the chart. The app draws to whatever depth the live chart
            happens to be expanded to, which a server has no way to see — so a scheduled export, or
            one from a colleague's tab, would silently be a different deck.
          */}
          <p className="muted small">
            The app draws to however far you have expanded the chart; here it is asked for, so the
            same request always produces the same deck.
          </p>

          <fieldset data-testid="export-chapters">
            <legend>Chapters</legend>
            {CHAPTERS.map((c) => (
              <label className="check" key={c.key}>
                <input
                  type="checkbox"
                  data-testid={`export-chapter-${c.key}`}
                  checked={deck[c.key]}
                  onChange={(e) => set(c.key, e.target.checked)}
                />
                <span>
                  {c.label}
                  <span className="muted small"> — {c.note}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {deck.bridge && (
            <label>
              Bridge measured in
              <select
                data-testid="export-bridge-metric"
                value={deck.bridgeMetric}
                onChange={(e) =>
                  set('bridgeMetric', e.target.value as DeckRequestOptions['bridgeMetric'])
                }
              >
                <option value="hc">Headcount</option>
                <option value="fte">FTE</option>
                <option value="cost">Cost</option>
              </select>
            </label>
          )}

          <p>
            <a
              className="btn"
              data-testid="export-download-pptx"
              href={pptxHref}
              onClick={onClose}
            >
              Download .pptx
            </a>
          </p>
        </div>
      )}
    </Modal>
  );
}
